import { useEffect, useRef } from 'react'
import { Briefcase, GraduationCap } from 'lucide-react'
import { EDUCATION, PROFESSIONAL_EXPERIENCE } from '../data/experience'
import { setupPanelReveals } from '../lib/motion'

const ExperienceTimeline: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    return setupPanelReveals(node, { drawLine: true })
  }, [])

  return (
    <div ref={ref} className="relative pl-7 md:pl-10">
      <div
        className="absolute left-[7px] md:left-[9px] top-2 bottom-2 w-px bg-neutral-200 dark:bg-white/10"
        aria-hidden="true"
      />
      <div
        data-timeline-line
        className="absolute left-[7px] md:left-[9px] top-2 bottom-2 w-px bg-primary-500/70"
        aria-hidden="true"
      />

      <div data-reveal-item className="relative mb-6 flex items-center gap-2.5">
        <span className="absolute -left-7 md:-left-10 top-[5px] h-[9px] w-[9px] rounded-full bg-primary-500" />
        <Briefcase size={15} className="text-primary-500" />
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
          Professional
        </span>
      </div>

      {PROFESSIONAL_EXPERIENCE.map((job) => (
        <div key={job.title} data-reveal-item className="relative mb-12">
          <span className="absolute -left-7 md:-left-10 top-0 flex items-center justify-center w-[15px] h-[15px] md:w-[19px] md:h-[19px] rounded-full bg-primary-500 ring-4 ring-primary-500/15" />

          <p className="mb-3 font-mono text-xs uppercase tracking-[0.16em] text-neutral-400 dark:text-neutral-500">
            {job.period}
          </p>

          <h3 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">
            {job.title}
            <span className="text-neutral-400 dark:text-neutral-500"> — {job.company}</span>
          </h3>

          <ul className="mt-5 grid gap-3 sm:grid-cols-2 max-w-4xl">
            {job.details.map((detail) => (
              <li
                key={detail}
                className="flex items-start gap-3 text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed"
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                {detail}
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap gap-2">
            {job.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-md border border-neutral-200 dark:border-white/8 px-2.5 py-1 font-mono text-xs text-neutral-500 dark:text-neutral-400"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      ))}

      <div data-reveal-item className="relative mb-6 flex items-center gap-2.5">
        <span className="absolute -left-7 md:-left-10 top-[5px] h-[9px] w-[9px] rounded-full bg-neutral-400 dark:bg-neutral-600" />
        <GraduationCap size={15} className="text-neutral-400 dark:text-neutral-500" />
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
          Education
        </span>
      </div>

      {EDUCATION.map((edu) => (
        <div key={edu.title} data-reveal-item className="relative mb-10 last:mb-0">
          <span className="absolute -left-7 md:-left-10 top-0 flex items-center justify-center w-[15px] h-[15px] md:w-[19px] md:h-[19px] rounded-full bg-neutral-300 dark:bg-neutral-700 ring-4 ring-neutral-300/20 dark:ring-neutral-700/20" />

          <p className="mb-2 font-mono text-xs uppercase tracking-[0.16em] text-neutral-400 dark:text-neutral-500">
            {edu.period}
          </p>

          <h4 className="font-display text-lg font-semibold tracking-tight">{edu.title}</h4>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {edu.org}
            {edu.honors && (
              <span className="ml-2 inline-flex items-center rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 text-[11px] font-mono uppercase tracking-[0.1em]">
                Honors
              </span>
            )}
          </p>
        </div>
      ))}
    </div>
  )
}

export default ExperienceTimeline